import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { delay, Observable, of, skipWhile } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { isString } from '@kros-sk/utils-fn/string';
import { KrosFormFieldModule } from '@kros-sk/ui/inputs';
import { NEW_DESIGN } from '@kros-sk/core/design-version';
import { SaveIndicatorModule, SaveIndicatorState } from '@kros-sk/ui/save-indicator';
import { TooltipDirective } from '@kros-sk/ui/tooltip';

import { KrosModalRef } from '../services/kros-modal-ref';

export interface MessageBoxProperties {
  caption: string | TemplateRef<any>;
  messageType: MessageTypes | Observable<MessageTypes>;
  acceptButton: string | TemplateRef<any>;
  cancelButton?: string | TemplateRef<any>;
  message?: string | TemplateRef<any>;
  icon?: string;
  focusedButton?: ButtonTypes;
  newDesign?: boolean | null;
  confirmation?: ConfirmSettings;
  customOverlayClass?: string | string[];
  customBodyClass?: string | string[];
}

export interface ConfirmSettings {
  label?: string;
  values: Array<string>;
  tooltip: string;
}

export enum MessageTypes {
  Danger = 'Danger',
  Warning = 'Warning',
  Primary = 'Primary',
}

export enum ButtonTypes {
  Cancel = 'cancel',
  Accept = 'accept',
}

@Component({
  selector: 'kros-message-box-modal',
  templateUrl: './message-box-modal.component.html',
  styleUrls: ['./message-box-modal.component.scss'],
  host: {
    '[tabindex]': '-1',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, KrosFormFieldModule, SaveIndicatorModule, TooltipDirective],
})
export class MessageBoxModalComponent implements AfterViewInit {
  #destroyRef = inject(DestroyRef);
  #elementRef = inject(ElementRef);
  #modalRef = inject(KrosModalRef);
  #newDesignToken = inject(NEW_DESIGN, { optional: true });

  @ViewChild('cancelButton') cancelButton!: ElementRef;
  @ViewChild('acceptButton') acceptButton!: ElementRef;

  protected msgBoxContext = {
    accept: this.#modalRef.submit.bind(this.#modalRef),
  };
  protected settings: MessageBoxProperties = this.#modalRef.data ?? null;
  protected acceptButtonReady$ = this.#modalRef.settings?.acceptButtonReady$ || of(true);
  protected showXButton = this.#modalRef.settings?.showXButton ?? true;
  protected confirmByText = this.#modalRef.settings?.confirmByText ?? false;
  protected SaveIndicatorState = SaveIndicatorState;
  protected btnCloseClass = this.effectiveNewDesign ? 'btn-outline-primary' : 'btn-outline-secondary';
  protected confirmationString = new FormControl('');

  protected messageType = this.#initializeMessageType();

  protected get effectiveNewDesign(): boolean {
    return this.settings?.newDesign ?? this.#newDesignToken ?? false;
  }

  ngAfterViewInit(): void {
    // focus modal to blur original focus on action button (e.g. print button)
    this.#elementRef.nativeElement.focus();
    this.acceptButtonReady$
      .pipe(
        skipWhile((ready) => !ready),
        delay(0), // wait for acceptButton to be enabled
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        if (!this.settings.focusedButton || this.settings.focusedButton === ButtonTypes.Cancel) {
          this.cancelButton?.nativeElement.focus();
        } else {
          this.acceptButton?.nativeElement.focus();
        }
      });
  }

  closeModal(): void {
    this.#modalRef.cancel();
  }

  submitModal(value: string): void {
    this.#modalRef.submit(value);
  }

  isString(value: unknown): value is string {
    return isString(value);
  }

  getTemplateRef(value: string | TemplateRef<any>): TemplateRef<any> | null {
    return typeof value === 'string' ? null : value;
  }

  protected isConfirmAllowed(): boolean {
    const confirmationValue = (this.confirmationString.value ?? '').trim().toLowerCase();
    return this.settings?.confirmation?.values
      ? this.settings.confirmation.values.some((allowedValue) => allowedValue.trim().toLowerCase() === confirmationValue)
      : true;
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(_event: PopStateEvent): void {
    this.closeModal();
  }

  get buttonType_v2(): string {
    return 'btn-' + this.messageType().toLowerCase();
  }

  get modalLineClass(): string {
    return 'modal-line-' + this.messageType()?.toLowerCase();
  }

  #initializeMessageType(): Signal<MessageTypes> {
    const messageType = this.settings.messageType;

    if (messageType instanceof Observable) {
      return toSignal(messageType, { initialValue: MessageTypes.Primary });
    }

    return signal(messageType ?? MessageTypes.Primary).asReadonly();
  }
}
